/**
 * Import Progress Indicator — a COMPLETED import (plain success).
 *
 * Every successful import gets one of these. Before, the panel only rendered a
 * completion when the job happened to carry an unknown-rows auto-ticket
 * (`CompletedTicketItem`), so an ordinary clean import ended with NO
 * confirmation at all — the card just vanished, or worse, sat frozen at 0%.
 *
 * It SETTLES: shown in full for a few seconds (the moment the user should
 * notice), then it collapses to one quiet line that still says what happened
 * and stays until dismissed.
 *
 * The record count is optional ON PURPOSE: it is rendered only when the job
 * payload actually reported one, so the card never invents a `0 registros`.
 */

import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { resolveSyncSourceLabel } from '@/platform/sync/sync-source-label'
import { userConfigService } from '@/shared/services/user-config.service'

import { ImportItemHeader } from './ImportItemHeader'
import { VenueTile } from './ImportRowParts'

/** How long a fresh completion stays in full before it collapses. */
export const COMPLETED_SETTLE_MS = 6000

interface CompletedImport {
  jobId: string
  sourceName: string
  completedAt: Date
  /** Rows booked by the job — null when the API did not report a count. */
  importedRecordCount: number | null
}

interface ImportCompletedItemProps {
  entry: CompletedImport
  onDismiss: () => void
  formatRelativeTime: (date: Date) => string
}

export const ImportCompletedItem = ({
  entry,
  onDismiss,
  formatRelativeTime,
}: ImportCompletedItemProps) => {
  const { t } = useTranslation('imports')
  const { t: tCommon } = useTranslation('common')
  const [settled, setSettled] = useState(false)

  useEffect(() => {
    const id = setTimeout(() => setSettled(true), COMPLETED_SETTLE_MS)
    return () => clearTimeout(id)
  }, [])

  return (
    <div
      className={`rounded-lg border motion-safe:transition-colors motion-safe:duration-500 ${
        settled
          ? 'border-transparent bg-transparent px-3 py-1.5'
          : 'border-crypto-success/30 bg-crypto-success/10 px-3 py-2.5'
      } space-y-1`}
      data-testid={`completed-import-item-${entry.jobId}`}
      data-settled={settled ? 'true' : 'false'}
      role="status"
    >
      <ImportItemHeader
        icon={
          <VenueTile label={resolveSyncSourceLabel(entry.sourceName, tCommon)} state="done" />
        }
        sourceName={entry.sourceName}
        timestampLabel={formatRelativeTime(entry.completedAt)}
        onDismiss={onDismiss}
        dismissTestId={`completed-import-dismiss-${entry.jobId}`}
        dismissHoverClassName="hover:bg-crypto-success/20"
      />

      <p className="pl-10 text-xs text-crypto-muted">
        <span className="text-crypto-success">
          {t('progressIndicator.state.done', 'Completed')}
        </span>
        {entry.importedRecordCount !== null && (
          <>
            {' '}
            <span className="tabular-nums" data-testid={`completed-import-records-${entry.jobId}`}>
              {/* ⚠️ `progressIndicator.records` es un plural de i18next
                  (`records_one`/`records_other`, con el número dentro en
                  `{{formatted}}`). Llamarlo SIN `count` impide elegir forma y cae
                  al valor por defecto inglés (medido: «2 records» en la UI
                  española). */}
              {t('progressIndicator.records', {
                count: entry.importedRecordCount,
                formatted: userConfigService.formatNumberWithFixedDecimals(
                  entry.importedRecordCount,
                  0,
                ),
              })}
            </span>
          </>
        )}
      </p>
    </div>
  )
}
